# Vue 3 component for NPS Kanban
<template>
  <div class="py-2 container-fluid">
    <div class="container-fluid">
      <!-- Show NPS Navigator if data exists -->
      <nps-navigator v-model="npsIndex" :title="npsTitle" :min="0" :max="items.length - 1" />

      <!-- Show import button if no data exists -->
      <div v-if="rawData.length === 0" class="empty-state-container">
        <div class="card">
          <div class="card-body text-center">
            <div class="empty-state-icon">
              <i class="fas fa-database"></i>
            </div>
            <h4>Nenhum NPS encontrado</h4>
            <p class="text-muted">Não existem dados de NPS no sistema.</p>
            <button @click="importNpsData" class="btn btn-primary btn-lg mt-3">
              <i class="fas fa-download me-2"></i> Importar dados de NPS
            </button>
          </div>
        </div>
      </div>

      <!-- Show kanban board if there's data -->
      <div v-else class="kanban-container">
        <div class="kanban-board" v-for="board of boards" :key="board.id">
          <div :class="['card', 'card-principal', `board-${board.id}`]">
            <div :class="['card-header', 'mb-0', 'px-2', 'py-2']">
              <h4 class="card-title mb-0 px-2">
                <div class="d-flex justify-content-between">
                  <span class="text-sm font-weight-bolder">
                    {{ board.name }}
                  </span>
                  <span class="text-sm">
                    {{ board.items.length }}
                  </span>
                </div>
              </h4>
              <nps-column-filters @filter-change="filters => handleFilterChange(board.id, filters)" />
            </div>
            <div class="card-body p-3">
              <div v-if="isLoading" class="text-center p-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Carregando...</span>
                </div>
              </div>
              <draggable v-else group="nps" @change="log" :list="board.items" :move="onMoveCallback" @end="onEnd"
                @start="onStart" item-key="id" class="dragglable" :id="board.id">
                <template #item="{ element }">
                  <nps-card-kanban v-if="element" :nps="element" :key="element.id" data-bs-toggle="modal"
                    data-bs-target="#modal-detalhes-nps-detratores" @click="setDetalhesCard(element)" />
                </template>
                <template #header>
                  <div v-if="board.items.length === 0" class="empty-column-placeholder">
                    Arraste os itens para esta coluna
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ModalAvaliacaoCliente :detalhes="detalhesNps"></ModalAvaliacaoCliente>
  </div>
</template>

<script>
import api from '@/services/api'
import draggable from 'vuedraggable';
import NpsCardKanban from './components/NpsCardKanban.vue';
import ModalAvaliacaoCliente from './components/ModalDetalhesNPS.vue';
import NpsColumnFilters from './components/NpsColumnFilters.vue';
import NpsNavigator from '../../dashboards/Nps/components/NpsNavigator.vue';
import { errorToast, successToast, showLoading } from '@/utils/alerts';

export default {
  name: "KanbanNPS",
  components: {
    draggable,
    NpsCardKanban,
    ModalAvaliacaoCliente,
    NpsColumnFilters,
    NpsNavigator
  },
  data() {
    return {
      npsIndex: 0,
      items: [],
      npsId: null,
      boardIds: {
        NOVO: 0,
        TRATATIVA: 1,
        RESOLVIDO: 2
      },
      boards: [],
      groupDestination: null,
      groupOrigin: null,
      detalhesNps: {},
      filters: {
        [0]: { searchQuery: '', sortBy: '' },
        [1]: { searchQuery: '', sortBy: '' },
        [2]: { searchQuery: '', sortBy: '' }
      },
      rawData: [], // Dados originais sem filtro
      isLoading: false
    }
  },
  created() {
    this.npsId = this.$route.query.nps;
    console.log(this.npsId);
  },
  mounted() {
    this.fetchNps();
  },
  methods: {
    async fetchNps() {
      this.isLoading = true;
      try {
        const response = await api.get('/nps');
        this.items = response.data;

        if (this.items.length > 0) {          
          if (this.npsId) {
            this.npsIndex = this.items.findIndex(item => item.id == this.npsId);
          } else {
            this.npsIndex = this.items.length - 1;
          }

          this.fetchData(this.currentNpsId);
        } else {
          this.isLoading = false;
        }
      } catch (error) {
        errorToast('Erro ao buscar lista de NPS');
        this.isLoading = false;
      }
    },
    async fetchData(quizId) {
      this.isLoading = true;
      try {
        const response = await api.get('/nps/kanban-detratores/' + quizId);
        this.rawData = response.data;
        this.setBoards(response.data);
      } catch (error) {
        errorToast('Erro ao carregar os dados');
      } finally {
        this.isLoading = false;
      }
    },
    setBoards(data) {
      this.boards = [
        {
          id: this.boardIds.NOVO,
          name: 'Novo',
          items: this.applyFilters(data.filter(item => item.status === 'NOVO'), this.boardIds.NOVO)
        },
        {
          id: this.boardIds.TRATATIVA,
          name: 'Tratativa',
          items: this.applyFilters(data.filter(item => item.status === 'TRATATIVA'), this.boardIds.TRATATIVA)
        },
        {
          id: this.boardIds.RESOLVIDO,
          name: 'Resolvido',
          items: this.applyFilters(data.filter(item => item.status === 'RESOLVIDO'), this.boardIds.RESOLVIDO)
        }
      ];
    },
    applyFilters(items, boardId) {
      let filteredItems = [...items];
      const { searchQuery, sortBy } = this.filters[boardId];

      // Aplicar busca
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredItems = filteredItems.filter(item =>
          item.nome_empresa.toLowerCase().includes(query) ||
          String(item.cliente_id_painel).includes(query) ||
          String(item.documento).includes(query)
        );
      }

      // Aplicar ordenação
      if (sortBy) {
        filteredItems.sort((a, b) => {
          switch (sortBy) {
            case 'media_notas_asc':
              return a.media_notas - b.media_notas;
            case 'media_notas_desc':
              return b.media_notas - a.media_notas;
            case 'respostas_asc':
              return a.respostas - b.respostas;
            case 'respostas_desc':
              return b.respostas - a.respostas;
            default:
              return 0;
          }
        });
      }

      return filteredItems;
    },
    handleFilterChange(boardId, filters) {
      this.filters[boardId] = filters;
      this.setBoards(this.rawData);
    },
    log(event) {
      if (event.added) {
        const { element } = event.added;
        const { groupDestination } = this;
        if (groupDestination !== null) {
          const status = this.getStatusFromBoardId(parseInt(groupDestination));
          if (status !== null) {
            this.updateStatus(element, status);
          } else {
            this.revertChanges();
          }
        }
      }
    },
    getStatusFromBoardId(boardId) {
      switch (boardId) {
        case this.boardIds.NOVO:
          return 0;
        case this.boardIds.TRATATIVA:
          return 1;
        case this.boardIds.RESOLVIDO:
          return 2;
        default:
          return null;
      }
    },
    revertChanges() {
      this.fetchData(this.currentNpsId);
      errorToast('Não foi possível mover o item');
    },
    async updateStatus(item, newStatus) {
      try {
        await api.patch(`/nps/detratores/${item.hotel_quiz_id}/${item.empresa_hotel_id}/status`, {
          status: newStatus
        });

        // Atualiza o status de todas as respostas no objeto local
        item.status = this.getStatusString(newStatus);
        successToast('Status atualizado com sucesso!');
      } catch (error) {
        console.log(error);
        errorToast('Erro ao atualizar o status');
        this.revertChanges();
      }
    },
    getStatusString(status) {
      switch (status) {
        case 0:
          return 'NOVO';
        case 1:
          return 'TRATATIVA';
        case 2:
          return 'RESOLVIDO';
        default:
          return 'NOVO';
      }
    },
    onMoveCallback(event) {
      const { to, from } = event;
      this.groupDestination = to.id;
      this.groupOrigin = from.id;
    },
    onStart() { },
    onEnd() { },
    setDetalhesCard(item) {
      this.detalhesNps = item;
    },
    formatarData(date) {
      if (!date) return '';
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      return date.toLocaleDateString('pt-BR', options);
    },
    async importNpsData() {
      try {

        const npsId = this.currentNpsId;

        const loading = showLoading(`Importando NPS #${npsId}, aguarde...`);

        try {
          // Importar o NPS específico
          await api.post('/nps/importar', { npsId });
          loading.close();
          successToast('NPS importado com sucesso!');

          // Refresh data
          await this.fetchNps();
        } catch (error) {
          loading.close();
          errorToast('Erro ao importar dados de NPS');
        }
      } catch (error) {
        errorToast('Erro ao buscar informações sobre NPS disponíveis');
      }
    }
  },
  computed: {
    currentNpsId() {
      return this.items[this.npsIndex]?.id;
    },
    npsTitle() {
      const nps = this.items[this.npsIndex];

      if (!nps) {
        return 'NPS ***';
      }

      return `NPS ${nps.id} - (${this.formatarData(new Date(nps.createdAt))} ~ ${this.formatarData(new Date(nps.dataFinal))})`;
    }
  },
  watch: {
    npsIndex: function () {
      this.fetchData(this.currentNpsId);

      this.$router.replace({
        name: 'Kanban NPS',
        query: {
          nps: this.currentNpsId
        }
      });

      console.log(this.$router);

      this.filters = {
        [0]: { searchQuery: '', sortBy: '' },
        [1]: { searchQuery: '', sortBy: '' },
        [2]: { searchQuery: '', sortBy: '' }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.filter-bar {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  padding: 12px 20px;
  border: 1px solid #e9ecef;
}

.empty-state-container {
  max-width: 600px;
  margin: 0 auto;

  .card {
    border-radius: 0.75rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.04);
  }

  .empty-state-icon {
    font-size: 3rem;
    color: #5e72e4;
    margin-bottom: 1rem;
  }
}

.kanban-container {
  display: flex;
  gap: 8px;
  padding: 1rem;
  overflow-y: auto;
  overflow-x: auto;

  .kanban-board {
    flex: 1;
    min-width: 300px;
    width: auto;

    .card {
      border-radius: 0.75rem;
      display: flex;
      flex-direction: column;

      .card-body {
        flex: 1;
        overflow-y: auto;
      }
    }
  }
}

.dragglable {
  height: calc(100vh - 250px);
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  overflow-y: auto;
}

.empty-column-placeholder {
  padding: 20px;
  text-align: center;
  color: #6c757d;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  margin: 10px 0;
  background-color: rgba(255, 255, 255, 0.5);
}

.card-principal {
  &.board-0 {
    border-top: 5px solid #f5365c;
  }

  &.board-1 {
    border-top: 5px solid #fbe840;
  }

  &.board-2 {
    border-top: 5px solid #2d58ce;
  }
}
</style>